// src/components/artwork-zoom/ArtworkZoomCard.tsx
//
// Phase 5E — renders an [ARTWORK_ZOOM] block. A Wikipedia-
// resolved image with annotated regions overlaid as subtle
// highlight rectangles. Each region is tappable: tap → opens a
// zoom view focused on that region, with the AI's note as
// caption.
//
// The point: when the AI's claim is about a SPECIFIC detail in
// an image ("look at the third figure from the left"), the
// existing inline-image card forces the user to squint at a
// 200px thumbnail. This component lets the AI direct attention
// to the actual region, and lets the user actually see it.
//
// Render strategy:
//
//   1. Show the full image with thin region rectangles overlaid.
//      Each rectangle has a small numbered chip in its
//      top-left corner. Hovering / tapping highlights the
//      rectangle's outline.
//
//   2. Below the image, a horizontal list of the regions as
//      labeled chips. Tapping a chip opens the zoom dialog.
//
//   3. Zoom dialog: full-screen on mobile, large modal on
//      desktop. Shows the full-resolution image, but the
//      transform-origin and scale are set so the chosen region
//      fills the visible area. The user can swipe between
//      regions via left/right arrow chips. The AI's note shows
//      as the caption.
//
// Coordinates: all x/y/w/h are normalized to [0, 1] of the
// image's natural dimensions. Conversion to pixel positions
// happens at render time using the rendered image's bounding
// box (so overlays scale with the image).

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Dialog,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  ChevronLeft,
  ChevronRight,
  OpenInNew,
} from '@mui/icons-material';
import { useEntityImage } from '../../hooks/useEntityImage';
import { useT } from '../../i18n/useT';
import type {
  ArtworkZoomBlock,
  ArtworkZoomRegion,
} from '../../utils/interactiveContent';

interface Props {
  block: ArtworkZoomBlock;
}

export const ArtworkZoomCard: React.FC<Props> = ({ block }) => {
  const t = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const image = useEntityImage('painting', block.wikipedia_title);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);

  if (!image) return null;

  return (
    <>
      <Box
        sx={{
          my: 2,
          p: 1.75,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1.25, color: 'text.primary' }}
        >
          {image.title}{' '}
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontWeight: 400 }}
          >
            · {t('tap a detail to zoom')}
          </Box>
        </Typography>

        {/* Image with overlay rectangles. Box is position:relative
            so absolutely-positioned rectangles stack on top. */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'action.hover',
          }}
        >
          <Box
            component="img"
            src={image.thumbnailUrl}
            alt={image.title}
            loading="lazy"
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {/* Region overlays */}
          {block.regions.map((r, idx) => (
            <ButtonBase
              key={`${r.label}-${idx}`}
              onClick={() => setZoomIdx(idx)}
              sx={{
                position: 'absolute',
                left: `${r.x * 100}%`,
                top: `${r.y * 100}%`,
                width: `${r.width * 100}%`,
                height: `${r.height * 100}%`,
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.06)',
                transition: 'background-color 0.15s ease, transform 0.05s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.16)',
                },
                '&:active': {
                  transform: 'scale(0.99)',
                },
              }}
              aria-label={t('Zoom to {region}', { region: r.label })}
            >
              {/* Numbered chip in the region's top-left corner.
                  Positioned absolutely OUTSIDE the rectangle
                  (translated up-and-left) so it doesn't cover
                  what's inside the region. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  left: -8,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                }}
              >
                {idx + 1}
              </Box>
            </ButtonBase>
          ))}
        </Box>

        {/* Region label chips below the image. Same numbering as
            the in-image overlays. Tap → open zoom dialog. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1.25,
          }}
        >
          {block.regions.map((r, idx) => (
            <Chip
              key={`${r.label}-chip-${idx}`}
              label={`${idx + 1}. ${r.label}`}
              size="small"
              onClick={() => setZoomIdx(idx)}
              sx={{
                fontWeight: 500,
                cursor: 'pointer',
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            />
          ))}
        </Box>
      </Box>

      <Dialog
        open={zoomIdx !== null}
        onClose={() => setZoomIdx(null)}
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
            borderRadius: isMobile ? 0 : 2,
            overflow: 'hidden',
            ...(isMobile ? {} : { width: 720, maxWidth: '92vw' }),
          },
        }}
      >
        {zoomIdx !== null && (
          <ZoomView
            image={image}
            regions={block.regions}
            initialIdx={zoomIdx}
            subject={image.title}
            onClose={() => setZoomIdx(null)}
          />
        )}
      </Dialog>
    </>
  );
};

/** Zoom dialog body. Renders the full-resolution image at a
 *  CSS transform that places the selected region at the center
 *  of the visible viewport. Left/right chips swipe between
 *  regions; close X dismisses. */
const ZoomView: React.FC<{
  image: { fullUrl: string; thumbnailUrl: string; title: string; articleUrl: string };
  regions: ArtworkZoomRegion[];
  initialIdx: number;
  subject: string;
  onClose: () => void;
}> = ({ image, regions, initialIdx, subject, onClose }) => {
  const t = useT();
  const theme = useTheme();
  const [idx, setIdx] = useState(initialIdx);
  const region = regions[idx];

  // Compute the transform-origin and scale to focus on the
  // chosen region. We scale so the region fills ~70% of the
  // viewable image area. transform-origin uses percentages
  // matching the region's center, so the chosen detail is
  // anchored when scaled.
  const transform = useMemo(() => {
    const TARGET_FRACTION = 0.7; // region fills 70% of viewport
    // Scale: how much we need to enlarge so the region is the
    // target fraction of the viewport. Capped at 6× to avoid
    // pixelation on small regions.
    const scaleX = TARGET_FRACTION / Math.max(0.02, region.width);
    const scaleY = TARGET_FRACTION / Math.max(0.02, region.height);
    const scale = Math.min(6, Math.min(scaleX, scaleY));
    // transform-origin at the region's center
    const originX = (region.x + region.width / 2) * 100;
    const originY = (region.y + region.height / 2) * 100;
    return {
      scale,
      origin: `${originX}% ${originY}%`,
    };
  }, [region]);

  const canPrev = idx > 0;
  const canNext = idx < regions.length - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 360,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {region.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subject} · {idx + 1} {t('of')} {regions.length}
          </Typography>
        </Box>
        {image.articleUrl && (
          <IconButton
            size="small"
            component="a"
            href={image.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('Open on Wikipedia')}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose} aria-label={t('Close')} size="small">
          <Close />
        </IconButton>
      </Box>

      {/* Zoom area — black background, image scaled+translated to
          focus on the region. */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          bgcolor: '#000',
          overflow: 'hidden',
          minHeight: 280,
        }}
      >
        <Box
          component="img"
          src={image.fullUrl}
          alt={`${region.label} — ${region.note}`}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transform: `scale(${transform.scale})`,
            transformOrigin: transform.origin,
            transition: 'transform 0.3s ease, transform-origin 0.3s ease',
          }}
        />
        {/* Prev / Next region affordances */}
        {canPrev && (
          <IconButton
            onClick={() => setIdx(idx - 1)}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            }}
            aria-label={t('Previous detail')}
          >
            <ChevronLeft />
          </IconButton>
        )}
        {canNext && (
          <IconButton
            onClick={() => setIdx(idx + 1)}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            }}
            aria-label={t('Next detail')}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>

      {/* Caption — AI's note about THIS region. */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          maxHeight: 140,
          overflowY: 'auto',
        }}
      >
        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
          {region.note}
        </Typography>
      </Box>
    </Box>
  );
};
